import React, { useState, useEffect } from 'react';
import api from '../../../api/axios';

const MedicalTestPackages = ({ onSelectPackage, selectedPackages = [] }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPackage, setExpandedPackage] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/medical-test-packages/');
      setPackages(response.data.data || response.data);
    } catch (err) {
      setError('Failed to fetch test packages');
      console.error('Error fetching packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePackageDetails = (packageId) => {
    setExpandedPackage(expandedPackage === packageId ? null : packageId);
  };

  const isSelected = (packageId) => {
    return selectedPackages.some(pkg => pkg.id === packageId);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent shadow-lg"></div>
          <div className="absolute inset-0 rounded-full h-12 w-12 border-4 border-gray-200"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 rounded-xl p-6 shadow-lg">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-red-800 font-semibold">{error}</p>
            <button 
              onClick={fetchPackages}
              className="mt-2 text-sm text-red-600 hover:text-red-800 underline font-medium transition-colors"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header Section */}
      <div className="text-center">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent mb-4">
          Test Packages
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Save money with our comprehensive test packages designed for complete health monitoring
        </p>
      </div>
      
      {/* Packages Grid */}
      <div className="grid gap-8 md:grid-cols-2">
        {packages.map((pkg) => (
          <div 
            key={pkg.id}
            className={`group relative bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border-2 ${
              isSelected(pkg.id) 
                ? 'border-purple-500 ring-4 ring-purple-100 bg-gradient-to-br from-purple-50 to-pink-50' 
                : 'border-gray-200 hover:border-purple-300'
            }`}
          >
            {/* Selection Button */}
            <div className="absolute top-4 left-4 z-20">
              {isSelected(pkg.id) ? (
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              ) : (
                <button 
                  className="w-8 h-8 border-2 border-gray-300 rounded-full hover:border-purple-500 hover:scale-110 transition-all duration-200"
                  onClick={() => onSelectPackage && onSelectPackage(pkg)}
                />
              )}
            </div>

            {/* Premium Badge */}
            {pkg.offer_price && (
              <div className="absolute top-4 right-4 z-10">
                <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                  SAVE {Math.round(((pkg.total_price - pkg.offer_price) / pkg.total_price) * 100)}%
                </div>
              </div>
            )}

            {/* Header Section */}
            <div className="p-8 bg-gradient-to-br from-purple-50 via-pink-50 to-red-50 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -translate-y-16 translate-x-16"></div>
              
              <div className="relative z-10">
                <div className="flex items-start justify-between mb-4">
                  <div className="pl-12">
                    <div className="w-14 h-14 bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <svg className="w-7 h-7 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {pkg.name}
                    </h3>
                    <div className="flex items-center space-x-2 text-sm text-purple-600">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-semibold">{pkg.tests?.length || 0} tests included</span>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    {pkg.offer_price ? (
                      <div className="space-y-1">
                        <span className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                          ₹{pkg.offer_price}
                        </span>
                        <div className="text-lg text-gray-500 line-through">
                          ₹{pkg.total_price}
                        </div>
                        <div className="text-sm text-green-600 font-semibold">
                          You save ₹{pkg.total_price - pkg.offer_price}
                        </div>
                      </div>
                    ) : (
                      <span className="text-3xl font-bold text-gray-900">
                        ₹{pkg.total_price}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Content Section */}
            <div className="p-8">
              {pkg.description && (
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {pkg.description}
                </p>
              )}
              
              {/* Package Highlights */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900 flex items-center">
                    <div className="w-1 h-5 bg-gradient-to-b from-purple-500 to-pink-500 rounded-full mr-3"></div>
                    Package Highlights
                  </h4>
                  <button
                    onClick={() => togglePackageDetails(pkg.id)}
                    className="text-purple-600 hover:text-purple-700 text-sm font-semibold flex items-center transition-colors"
                  >
                    {expandedPackage === pkg.id ? 'Hide' : 'View'} Details
                    <svg 
                      className={`ml-1 w-4 h-4 transition-transform duration-200 ${
                        expandedPackage === pkg.id ? 'rotate-180' : ''
                      }`}
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>

                {/* Quick Benefits */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="flex items-center text-sm text-gray-700 bg-purple-50 rounded-lg p-3">
                    <svg className="w-4 h-4 text-purple-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">Comprehensive</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-700 bg-green-50 rounded-lg p-3">
                    <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    <span className="font-medium">Cost Effective</span>
                  </div>
                </div>
                
                {/* Expanded Details */}
                {expandedPackage === pkg.id && (
                  <div className="border-t border-gray-200 pt-6 animate-fadeIn">
                    <h5 className="font-semibold text-gray-900 mb-4">Included Tests:</h5>
                    <div className="space-y-3">
                      {pkg.tests?.map((test, index) => (
                        <div key={test.id || index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                            <span className="text-gray-800 font-medium">{test.title}</span>
                          </div>
                          <span className="text-gray-600 font-semibold">
                            ₹{test.offer_price || test.price}
                          </span>
                        </div>
                      )) || (
                        <p className="text-gray-500 text-sm italic">No test details available</p>
                      )}
                    </div>

                    {/* Package Benefits */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
                      <h6 className="font-semibold text-purple-900 mb-3">Package Benefits:</h6>
                      <div className="grid grid-cols-1 gap-2">
                        {[
                          "🏠 Free home sample collection",
                          "📧 Digital reports via email",
                          "⏱️ Results within 24-48 hours",
                          "💳 Single payment for all tests"
                        ].map((benefit, idx) => (
                          <div key={idx} className="flex items-center text-sm text-purple-800">
                            <span>{benefit}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Action Button */}
              <button
                onClick={() => onSelectPackage && onSelectPackage(pkg)}
                className={`w-full font-bold py-4 px-6 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 text-lg ${
                  isSelected(pkg.id)
                    ? 'bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white'
                    : 'bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white'
                }`}
              >
                <span className="flex items-center justify-center">
                  {isSelected(pkg.id) ? (
                    <>
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      Selected ✓
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                      </svg>
                      Select Package
                    </>
                  )}
                </span>
              </button>

              {/* Value Proposition */}
              <div className="mt-4 text-center">
                <p className="text-sm text-gray-600">
                  🎯 Best value • 📊 Complete analysis • 🔒 Secure booking
                </p>
              </div>
            </div>

            {/* Hover Glow Effect */}
            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-sm -z-10"></div>
          </div>
        ))}
      </div>
      
      {/* Empty State */}
      {packages.length === 0 && !loading && (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gradient-to-r from-purple-100 to-pink-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-purple-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 4a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No test packages available</h3>
          <p className="text-gray-500">Please check back later for available packages.</p>
        </div>
      )}
    </div>
  );
};

export default MedicalTestPackages;